import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

/**
 * Custom hook that manages URL search parameters as a state object.
 *
 * @param {Object} [defaultInit] - Optional default initialization for search parameters.
 * @returns {[Object, Function]} - Returns an array with the current state object and a function to update it.
 */
const useAppSearchParams = (defaultInit) => {
	const [searchParams, setSearchParams] = useSearchParams(defaultInit);

	/**
	 * Converts the current search parameters to a plain object.
	 *
	 * @type {Object}
	 */
	const state = useMemo(
		() => Object.fromEntries([...searchParams]),
		[searchParams],
	);

	/**
	 * Updates the search parameters. It can accept either a new object
	 * or a callback function that receives the previous state and returns a new state object.
	 *
	 * @param {Object|Function} param - New search parameters as an object or a function that returns an object.
	 */
	const setState = useCallback(
		(param) => {
			if (param instanceof Function) {
				const newState = param(state);
				setSearchParams(newState);
			} else {
				setSearchParams(param);
			}
		},
		[setSearchParams, state],
	);

	return [state, setState];
};

export default useAppSearchParams;
